import paths from '../routes/paths'
import { hasPermission, routeAllowed, sidebarTokenAllowed } from '../access/accessPolicy'
import { buildAllowedSidebarModules, canShowUserManagementNav } from '../utils/companyModules'
import { isCompanyAdmin } from '../views/Account/account.utils'

const SUPER_ADMIN = 'Super Admin'

function hasModule(ctx, token) {
  return ctx.allowedModules.includes(token)
}

function open(ctx, token, path, permission) {
  if (token && !hasModule(ctx, token)) return false
  if (permission && !ctx.allow(permission)) return false
  return ctx.routeAllowed(path)
}

const invoiceSteps = {
  en: [
    'Open Sales.',
    'Choose Create Transaction, then Invoice.',
    'Select the customer, add the lines, and save. The invoice number is assigned when the invoice is saved.',
  ],
  es: [
    'Abre Ventas.',
    'Pulsa Crear Transacción y elige Factura.',
    'Selecciona el cliente, agrega las líneas y guarda. El número de la factura se asigna al guardarla.',
  ],
}

const estimateSteps = {
  en: [
    'Open Sales.',
    'Choose Create Transaction, then Estimate.',
    'Select the customer, add the lines, and save.',
  ],
  es: [
    'Abre Ventas.',
    'Pulsa Crear Transacción y elige Cotización.',
    'Selecciona el cliente, agrega las líneas y guarda.',
  ],
}

const productSteps = {
  en: ['Open Products.', 'Choose Create Product.', 'Enter the name and save.'],
  es: ['Abre Productos.', 'Elige Crear producto.', 'Escribe el nombre y guarda.'],
}

const customerSteps = {
  en: ['Open Customers.', 'Choose Create Customer.', 'Enter the name and save.'],
  es: ['Abre Clientes.', 'Elige Crear cliente.', 'Escribe el nombre y guarda.'],
}

/**
 * Navigable functions of this app. Search and the company assistant only
 * offer a row when `visible` is true for the signed-in company and user.
 */
export const FEATURES = [
  {
    id: 'dashboard',
    kind: 'open',
    group: { en: 'Home', es: 'Inicio' },
    label: { en: 'Dashboard', es: 'Tablero' },
    path: paths.dashboard,
    keywords: ['dashboard', 'home', 'inicio', 'tablero'],
    visible: (ctx) => ctx.routeAllowed(paths.dashboard),
  },
  {
    id: 'notifications',
    kind: 'open',
    group: { en: 'Home', es: 'Inicio' },
    label: { en: 'Notifications', es: 'Notificaciones' },
    path: paths.notifications,
    keywords: ['notifications', 'notificaciones', 'alerts', 'alertas'],
    visible: (ctx) => ctx.routeAllowed(paths.notifications),
  },
  {
    id: 'pos',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'POS', es: 'Punto de venta' },
    path: paths.pos,
    keywords: ['pos', 'point of sale', 'punto de venta', 'caja'],
    visible: (ctx) => ctx.canAccessPlanModule('pos') && ctx.sidebarTokenAllowed('pos') && ctx.routeAllowed(paths.pos),
  },
  {
    id: 'users',
    kind: 'open',
    group: { en: 'User Management', es: 'Usuarios' },
    label: { en: 'Users', es: 'Usuarios' },
    path: paths.users.list,
    keywords: ['users', 'usuarios', 'staff', 'empleados'],
    visible: (ctx) => ctx.canShowUsers && ctx.routeAllowed(paths.users.list),
  },
  {
    id: 'create-user',
    kind: 'create',
    group: { en: 'User Management', es: 'Usuarios' },
    label: { en: 'Create user', es: 'Crear usuario' },
    path: paths.users.new,
    keywords: ['user', 'usuario', 'create', 'crear'],
    visible: (ctx) => ctx.canShowUsers && ctx.routeAllowed(paths.users.new),
  },
  {
    id: 'products',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Products', es: 'Productos' },
    path: paths.products.list,
    keywords: ['products', 'productos', 'items', 'articulos', 'artículos', 'catalog', 'catalogo', 'catálogo'],
    visible: (ctx) => open(ctx, 'Products', paths.products.list),
  },
  {
    id: 'create-product',
    kind: 'create',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Create product', es: 'Crear producto' },
    path: paths.products.new,
    keywords: ['product', 'producto', 'item', 'articulo', 'artículo'],
    steps: productSteps,
    visible: (ctx) => open(ctx, 'Products', paths.products.new, 'catalog.create'),
  },
  {
    id: 'labors',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Labors', es: 'Mano de obra' },
    path: paths.labors.list,
    keywords: ['labors', 'labor', 'mano de obra', 'servicio'],
    visible: (ctx) => open(ctx, 'Products', paths.labors.list, 'catalog.view'),
  },
  {
    id: 'create-labor',
    kind: 'create',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Create labor', es: 'Crear mano de obra' },
    path: paths.labors.new,
    keywords: ['labor', 'mano de obra'],
    visible: (ctx) => open(ctx, 'Products', paths.labors.new, 'catalog.create'),
  },
  {
    id: 'brands',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Brands', es: 'Marcas' },
    path: paths.brands.list,
    keywords: ['brands', 'marcas'],
    visible: (ctx) => open(ctx, 'Products', paths.brands.list),
  },
  {
    id: 'product-tags',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Product tags', es: 'Etiquetas de producto' },
    path: paths.productTags.list,
    keywords: ['tags', 'etiquetas'],
    visible: (ctx) => open(ctx, 'Products', paths.productTags.list),
  },
  {
    id: 'product-badges',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Product badges', es: 'Insignias de producto' },
    path: paths.productBadges.list,
    keywords: ['badges', 'insignias'],
    visible: (ctx) => open(ctx, 'Products', paths.productBadges.list),
  },
  {
    id: 'attributes',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Attributes', es: 'Atributos' },
    path: paths.attributeDefinitions.list,
    keywords: ['attributes', 'atributos'],
    visible: (ctx) => open(ctx, 'Products', paths.attributeDefinitions.list),
  },
  {
    id: 'categories',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Categories', es: 'Categorías' },
    path: paths.categories.list,
    keywords: ['categories', 'categorias', 'categorías'],
    visible: (ctx) => open(ctx, 'Products', paths.categories.list),
  },
  {
    id: 'locations',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Storage locations', es: 'Ubicaciones' },
    path: paths.locations.list,
    keywords: ['locations', 'ubicaciones', 'storage', 'almacen'],
    visible: (ctx) => open(ctx, 'Products', paths.locations.list),
  },
  {
    id: 'uoms',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Units of measure', es: 'Unidades de medida' },
    path: paths.uoms.list,
    keywords: ['uom', 'units', 'unidades'],
    visible: (ctx) => open(ctx, 'Products', paths.uoms.list),
  },
  {
    id: 'taxes',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Taxes', es: 'Impuestos' },
    path: paths.taxes.list,
    keywords: ['taxes', 'tax', 'impuestos', 'impuesto'],
    visible: (ctx) => hasModule(ctx, 'Products') && !ctx.profileLocked && ctx.routeAllowed(paths.taxes.list),
  },
  {
    id: 'pay-terms',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Pay terms', es: 'Términos de pago' },
    path: paths.payTerms.list,
    keywords: ['pay terms', 'terminos', 'términos', 'payment terms'],
    visible: (ctx) => open(ctx, 'Products', paths.payTerms.list),
  },
  {
    id: 'work-order-types',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Work order types', es: 'Tipos de orden de trabajo' },
    path: paths.workOrderTypes.list,
    keywords: ['work order types', 'tipos de orden'],
    visible: (ctx) => open(ctx, 'Products', paths.workOrderTypes.list),
  },
  {
    id: 'barcode',
    kind: 'open',
    group: { en: 'Products', es: 'Productos' },
    label: { en: 'Barcode generator', es: 'Generador de códigos' },
    path: paths.barcodeGenerator,
    keywords: ['barcode', 'codigo de barras', 'código de barras'],
    visible: (ctx) => open(ctx, 'Products', paths.barcodeGenerator),
  },
  {
    id: 'warehouses',
    kind: 'open',
    group: { en: 'Warehouses', es: 'Almacenes' },
    label: { en: 'Warehouses', es: 'Almacenes' },
    path: paths.warehouses.list,
    keywords: ['warehouses', 'warehouse', 'almacenes', 'almacen', 'almacén'],
    visible: (ctx) => open(ctx, 'WareHouse', paths.warehouses.list),
  },
  {
    id: 'create-warehouse',
    kind: 'create',
    group: { en: 'Warehouses', es: 'Almacenes' },
    label: { en: 'Create warehouse', es: 'Crear almacén' },
    path: paths.warehouses.new,
    keywords: ['warehouse', 'almacen', 'almacén'],
    visible: (ctx) => hasModule(ctx, 'WareHouse') && (!ctx.profileLocked || ctx.companyAdmin) && ctx.routeAllowed(paths.warehouses.new),
  },
  {
    id: 'vendors',
    kind: 'open',
    group: { en: 'Vendors', es: 'Proveedores' },
    label: { en: 'Vendors', es: 'Proveedores' },
    path: paths.vendors.list,
    keywords: ['vendors', 'vendor', 'proveedores', 'proveedor', 'supplier'],
    visible: (ctx) => open(ctx, 'Vendor', paths.vendors.list),
  },
  {
    id: 'create-vendor',
    kind: 'create',
    group: { en: 'Vendors', es: 'Proveedores' },
    label: { en: 'Create vendor', es: 'Crear proveedor' },
    path: paths.vendors.new,
    keywords: ['vendor', 'proveedor', 'supplier'],
    visible: (ctx) => open(ctx, 'Vendor', paths.vendors.new),
  },
  {
    id: 'stock-receipts',
    kind: 'open',
    group: { en: 'Inventory', es: 'Inventario' },
    label: { en: 'Stock receipts', es: 'Recepciones' },
    path: paths.stockReceipts.list,
    keywords: ['stock receipt', 'receipts', 'recepciones', 'recepcion', 'recepción', 'stock', 'inventario', 'agregar stock', 'add stock', 'anadir stock'],
    steps: {
      en: ['Open Stock Receipts.', 'Create a receipt and add the product lines.', 'Choose Post Receipt when the quantities are confirmed. Posting adds the stock.'],
      es: ['Abre Recepciones.', 'Crea una recepción y agrega las líneas.', 'Pulsa Publicar recepción cuando las cantidades estén revisadas. Publicar agrega el stock.'],
    },
    visible: (ctx) => open(ctx, 'Stock Receipt', paths.stockReceipts.list),
  },
  {
    id: 'create-stock-receipt',
    kind: 'create',
    group: { en: 'Inventory', es: 'Inventario' },
    label: { en: 'Create stock receipt', es: 'Crear recepción' },
    path: paths.stockReceipts.new,
    keywords: ['stock receipt', 'recepcion', 'recepción', 'add stock', 'agregar stock', 'anadir stock'],
    steps: {
      en: ['Open Stock Receipts.', 'Choose Create Stock Receipt.', 'Add the lines and post the receipt. The Vendor Invoice # is the supplier document number, not a vendor bill.'],
      es: ['Abre Recepciones.', 'Elige Crear recepción.', 'Agrega las líneas y publica. El número de factura del proveedor es el documento del proveedor, no una factura de proveedor.'],
    },
    visible: (ctx) => open(ctx, 'Stock Receipt', paths.stockReceipts.new),
  },
  {
    id: 'transfers',
    kind: 'open',
    group: { en: 'Inventory', es: 'Inventario' },
    label: { en: 'Transfers', es: 'Transferencias' },
    path: paths.stockTransfers.list,
    keywords: ['transfers', 'transfer', 'transferencias', 'transferencia', 'transferir', 'warehouses', 'almacenes', 'between warehouses', 'entre almacenes'],
    visible: (ctx) => (hasModule(ctx, 'Stock Transfer') || hasModule(ctx, 'Stock Receipt')) && ctx.routeAllowed(paths.stockTransfers.list),
  },
  {
    id: 'create-transfer',
    kind: 'create',
    group: { en: 'Inventory', es: 'Inventario' },
    label: { en: 'New transfer', es: 'Nueva transferencia' },
    path: paths.stockTransfers.new,
    keywords: ['transfer', 'transferir', 'transferencia', 'warehouses', 'almacenes', 'between warehouses', 'entre almacenes'],
    steps: {
      en: ['Open Transfers.', 'Choose New Transfer.', 'Select the product, the warehouse it leaves, and the warehouse it enters.'],
      es: ['Abre Transferencias.', 'Elige Nueva transferencia.', 'Selecciona el producto, el almacén de salida y el almacén de entrada.'],
    },
    visible: (ctx) => (hasModule(ctx, 'Stock Transfer') || hasModule(ctx, 'Stock Receipt')) && ctx.routeAllowed(paths.stockTransfers.new),
  },
  {
    id: 'purchase-orders',
    kind: 'open',
    group: { en: 'Purchasing', es: 'Compras' },
    label: { en: 'Purchase orders', es: 'Órdenes de compra' },
    path: paths.purchaseOrders.list,
    keywords: ['purchase order', 'purchase orders', 'orden de compra', 'ordenes de compra', 'órdenes de compra'],
    visible: (ctx) => open(ctx, 'Purchase Order', paths.purchaseOrders.list),
  },
  {
    id: 'create-purchase-order',
    kind: 'create',
    group: { en: 'Purchasing', es: 'Compras' },
    label: { en: 'New purchase order', es: 'Nueva orden de compra' },
    path: paths.purchaseOrders.new,
    keywords: ['purchase order', 'orden de compra'],
    visible: (ctx) => open(ctx, 'Purchase Order', paths.purchaseOrders.new),
  },
  {
    id: 'vendor-bills',
    kind: 'open',
    group: { en: 'Purchasing', es: 'Compras' },
    label: { en: 'Vendor bills', es: 'Facturas de proveedor' },
    path: paths.vendorBills.list,
    keywords: ['vendor bill', 'vendor invoice', 'bills', 'factura de proveedor', 'cuentas por pagar'],
    visible: (ctx) =>
      (ctx.planModules.includes('Expensive') || ctx.planModules.includes('Purchase Order')) &&
      ctx.allow('vendor_bill.view') &&
      ctx.routeAllowed(paths.vendorBills.list),
  },
  {
    id: 'create-vendor-bill',
    kind: 'create',
    group: { en: 'Purchasing', es: 'Compras' },
    label: { en: 'Create vendor bill', es: 'Crear factura de proveedor' },
    path: paths.vendorBills.new,
    keywords: ['vendor bill', 'vendor invoice', 'factura de proveedor', 'cuentas por pagar'],
    steps: {
      en: ['Open Vendor Bills.', 'Choose Create Vendor Bill.', 'Select the vendor, enter the vendor invoice number, add the lines, and save. A vendor bill is the accounts-payable invoice. It does not add stock.'],
      es: ['Abre Facturas de proveedor.', 'Elige Crear factura de proveedor.', 'Selecciona el proveedor, escribe su número de factura, agrega las líneas y guarda. Es la cuenta por pagar, no una factura de cliente ni una entrada de stock.'],
    },
    visible: (ctx) =>
      (ctx.planModules.includes('Expensive') || ctx.planModules.includes('Purchase Order')) &&
      ctx.allow('vendor_bill.view') &&
      ctx.routeAllowed(paths.vendorBills.new),
  },
  {
    id: 'adjustments',
    kind: 'open',
    group: { en: 'Inventory', es: 'Inventario' },
    label: { en: 'Adjustments', es: 'Ajustes' },
    path: paths.adjustments.list,
    keywords: ['adjustments', 'adjustment', 'ajustes', 'ajuste'],
    visible: (ctx) => open(ctx, 'Adjustment', paths.adjustments.list),
  },
  {
    id: 'create-adjustment',
    kind: 'create',
    group: { en: 'Inventory', es: 'Inventario' },
    label: { en: 'Create adjustment', es: 'Crear ajuste' },
    path: paths.adjustments.new,
    keywords: ['adjustment', 'ajuste'],
    visible: (ctx) => open(ctx, 'Adjustment', paths.adjustments.new),
  },
  {
    id: 'customers',
    kind: 'open',
    group: { en: 'Customers', es: 'Clientes' },
    label: { en: 'Customers', es: 'Clientes' },
    path: paths.customers.list,
    keywords: ['customers', 'customer', 'clientes', 'cliente'],
    visible: (ctx) => open(ctx, 'Costumers', paths.customers.list),
  },
  {
    id: 'create-customer',
    kind: 'create',
    group: { en: 'Customers', es: 'Clientes' },
    label: { en: 'Create customer', es: 'Crear cliente' },
    path: paths.customers.new,
    keywords: ['customer', 'cliente'],
    steps: customerSteps,
    visible: (ctx) => open(ctx, 'Costumers', paths.customers.new),
  },
  {
    id: 'sales',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Sales', es: 'Ventas' },
    path: paths.invoices.list,
    keywords: ['sales', 'ventas', 'invoices', 'facturas'],
    visible: (ctx) => open(ctx, 'Sales', paths.invoices.list),
  },
  {
    id: 'create-invoice',
    kind: 'create',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Create invoice', es: 'Crear factura' },
    path: paths.invoices.new,
    keywords: ['invoice', 'factura', 'facturar', 'bill'],
    steps: invoiceSteps,
    visible: (ctx) => open(ctx, 'Sales', paths.invoices.new, 'invoice.draft'),
  },
  {
    id: 'create-estimate',
    kind: 'create',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Create estimate', es: 'Crear cotización' },
    path: paths.estimates.new,
    keywords: ['estimate', 'estimates', 'cotizacion', 'cotización', 'presupuesto'],
    steps: estimateSteps,
    visible: (ctx) => open(ctx, 'Sales', paths.estimates.new, 'estimate.draft'),
  },
  {
    id: 'online-sales',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Online sales', es: 'Ventas en línea' },
    path: paths.onlineSales.list,
    keywords: ['online', 'ecommerce', 'tienda', 'en linea', 'en línea'],
    visible: (ctx) => open(ctx, 'Sales', paths.onlineSales.list, 'invoice.view'),
  },
  {
    id: 'sale-returns',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Returns', es: 'Devoluciones' },
    path: paths.saleReturns.list,
    keywords: ['returns', 'return', 'devoluciones', 'devolucion', 'devolución'],
    visible: (ctx) =>
      hasModule(ctx, 'Sales') &&
      (ctx.companyAdmin || (ctx.profileLocked && ctx.hasPermission('return.view'))) &&
      ctx.routeAllowed(paths.saleReturns.list),
  },
  {
    id: 'inquiries',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Inquiries', es: 'Consultas' },
    path: paths.storefrontInquiries.list,
    keywords: ['inquiries', 'consultas', 'messages', 'mensajes'],
    visible: (ctx) => open(ctx, 'Sales', paths.storefrontInquiries.list, 'invoice.view'),
  },
  {
    id: 'recurring-invoices',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Recurring invoices', es: 'Facturas recurrentes' },
    path: paths.recurringInvoices.list,
    keywords: ['recurring', 'recurrentes', 'subscription invoice'],
    visible: (ctx) => open(ctx, 'Sales', paths.recurringInvoices.list, 'invoice.view'),
  },
  {
    id: 'work-orders',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Work orders', es: 'Órdenes de trabajo' },
    path: paths.workOrders.list,
    keywords: ['work order', 'work orders', 'orden de trabajo', 'ordenes de trabajo', 'órdenes de trabajo'],
    visible: (ctx) => open(ctx, 'Sales', paths.workOrders.list, 'work_order.view'),
  },
  {
    id: 'create-work-order',
    kind: 'create',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Create work order', es: 'Crear orden de trabajo' },
    path: paths.workOrders.new,
    keywords: ['work order', 'orden de trabajo'],
    visible: (ctx) => open(ctx, 'Sales', paths.workOrders.new, 'work_order.draft'),
  },
  {
    id: 'receive-payments',
    kind: 'open',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Receive payment', es: 'Recibir pago' },
    path: paths.receivePayments.list,
    keywords: ['receive payment', 'payments', 'cobros', 'cobro', 'pagos'],
    visible: (ctx) => open(ctx, 'Sales', paths.receivePayments.list, 'payment.view'),
  },
  {
    id: 'create-payment',
    kind: 'create',
    group: { en: 'Sales', es: 'Ventas' },
    label: { en: 'Record payment', es: 'Registrar pago' },
    path: paths.payments.new,
    keywords: ['payment', 'pago', 'cobro', 'receive payment'],
    steps: {
      en: ['Open Sales.', 'Choose Create Transaction, then Receive Payment.', 'Select the customer and the invoice, then save.'],
      es: ['Abre Ventas.', 'Pulsa Crear Transacción y elige Recibir pago.', 'Selecciona el cliente y la factura, y guarda.'],
    },
    visible: (ctx) => open(ctx, 'Sales', paths.payments.new, 'payment.record'),
  },
  {
    id: 'expenses',
    kind: 'open',
    group: { en: 'Expenses', es: 'Gastos' },
    label: { en: 'Expenses', es: 'Gastos' },
    path: paths.expenses.list,
    keywords: ['expenses', 'expense', 'gastos', 'gasto'],
    visible: (ctx) => open(ctx, 'Expensive', paths.expenses.list, 'expense.view'),
  },
  {
    id: 'create-expense',
    kind: 'create',
    group: { en: 'Expenses', es: 'Gastos' },
    label: { en: 'Create expense', es: 'Crear gasto' },
    path: paths.expenses.new,
    keywords: ['expense', 'gasto'],
    visible: (ctx) => open(ctx, 'Expensive', paths.expenses.new, 'expense.draft'),
  },
  {
    id: 'expense-categories',
    kind: 'open',
    group: { en: 'Expenses', es: 'Gastos' },
    label: { en: 'Expense categories', es: 'Categorías de gasto' },
    path: paths.expenseCategories.list,
    keywords: ['expense categories', 'categorias de gasto', 'categorías de gasto'],
    visible: (ctx) => open(ctx, 'Expensive', paths.expenseCategories.list, 'expense.view'),
  },
  {
    id: 'payment-accounts',
    kind: 'open',
    group: { en: 'Expenses', es: 'Gastos' },
    label: { en: 'Payment accounts', es: 'Cuentas de pago' },
    path: paths.paymentAccounts.list,
    keywords: ['payment account', 'cuentas de pago'],
    visible: (ctx) => open(ctx, 'Expensive', paths.paymentAccounts.list, 'payment.view'),
  },
  {
    id: 'payment-types',
    kind: 'open',
    group: { en: 'Expenses', es: 'Gastos' },
    label: { en: 'Payment types', es: 'Tipos de pago' },
    path: paths.paymentTypes.list,
    keywords: ['payment types', 'tipos de pago'],
    visible: (ctx) => open(ctx, 'Expensive', paths.paymentTypes.list, 'payment.view'),
  },
  {
    id: 'accounting',
    kind: 'open',
    group: { en: 'Accounting', es: 'Contabilidad' },
    label: { en: 'Accounting overview', es: 'Resumen contable' },
    path: paths.accounting.overview,
    keywords: ['accounting', 'contabilidad'],
    visible: (ctx) => ctx.canAccessPlanModule('reports') && ctx.sidebarTokenAllowed('Reports') && ctx.routeAllowed(paths.accounting.overview),
  },
  {
    id: 'profit-loss',
    kind: 'open',
    group: { en: 'Accounting', es: 'Contabilidad' },
    label: { en: 'Profit and loss', es: 'Ganancias y pérdidas' },
    path: paths.accounting.profitLoss,
    keywords: ['profit', 'loss', 'ganancias', 'perdidas', 'pérdidas'],
    visible: (ctx) => ctx.canAccessPlanModule('reports') && ctx.sidebarTokenAllowed('Reports') && ctx.routeAllowed(paths.accounting.profitLoss),
  },
  {
    id: 'sales-report',
    kind: 'open',
    group: { en: 'Accounting', es: 'Contabilidad' },
    label: { en: 'Sales report', es: 'Reporte de ventas' },
    path: paths.accounting.salesReport,
    keywords: ['sales report', 'reporte de ventas'],
    visible: (ctx) => ctx.canAccessPlanModule('reports') && ctx.sidebarTokenAllowed('Reports') && ctx.routeAllowed(paths.accounting.salesReport),
  },
  {
    id: 'social-content',
    kind: 'open',
    group: { en: 'Marketing', es: 'Marketing' },
    label: { en: 'Social media', es: 'Redes sociales' },
    path: paths.marketing.socialContent,
    keywords: ['social', 'redes', 'instagram', 'facebook'],
    visible: (ctx) => (ctx.allow('catalog.view') || ctx.allow('catalog.update')) && ctx.routeAllowed(paths.marketing.socialContent),
  },
  {
    id: 'creative-studio',
    kind: 'open',
    group: { en: 'Marketing', es: 'Marketing' },
    label: { en: 'Creative Studio', es: 'Estudio creativo' },
    path: paths.marketing.creativeStudio,
    keywords: ['creative', 'studio', 'estudio', 'images', 'imagenes', 'imágenes'],
    visible: (ctx) => (ctx.allow('catalog.view') || ctx.allow('catalog.update')) && ctx.routeAllowed(paths.marketing.creativeStudio),
  },
  {
    id: 'chart-of-accounts',
    kind: 'open',
    group: { en: 'Settings', es: 'Configuración' },
    label: { en: 'Chart of accounts', es: 'Catálogo de cuentas' },
    path: paths.settings.chartOfAccounts,
    keywords: ['chart of accounts', 'catalogo de cuentas', 'catálogo de cuentas'],
    visible: (ctx) => ctx.settingsSection && ctx.allow('report.view') && ctx.routeAllowed(paths.settings.chartOfAccounts),
  },
  {
    id: 'settings-payment',
    kind: 'open',
    group: { en: 'Settings', es: 'Configuración' },
    label: { en: 'Payment settings', es: 'Configuración de pagos' },
    path: paths.settings.payment,
    keywords: ['payment settings', 'stripe', 'paypal', 'pagos'],
    visible: (ctx) => ctx.settingsSection && ctx.companyAdmin && ctx.routeAllowed(paths.settings.payment),
  },
  {
    id: 'settings-account',
    kind: 'open',
    group: { en: 'Settings', es: 'Configuración' },
    label: { en: 'Account', es: 'Cuenta' },
    path: paths.settings.account,
    keywords: ['account', 'cuenta', 'profile', 'perfil'],
    visible: (ctx) => ctx.settingsSection && (!ctx.profileLocked || ctx.companyAdmin) && ctx.routeAllowed(paths.settings.account),
  },
  {
    id: 'admin-companies',
    kind: 'open',
    group: { en: 'Platform', es: 'Plataforma' },
    label: { en: 'Companies', es: 'Compañías' },
    path: paths.admin.companies,
    keywords: ['companies', 'companias', 'compañías'],
    visible: (ctx) => ctx.isSuperAdmin && ctx.routeAllowed(paths.admin.companies),
  },
]

export function buildFeatureContext({ userInfo, access, canAccessPlanModule }) {
  const safeAccess = access || { ready: false }
  const profileLocked = Boolean(safeAccess.profileEnforced && safeAccess.role !== 'admin' && !safeAccess.isAdmin)
  const planModules = buildAllowedSidebarModules(userInfo)
  const allowedModules = planModules.filter((token) => sidebarTokenAllowed(token, safeAccess))
  const roles = userInfo?.roles || []

  return {
    userInfo,
    access: safeAccess,
    profileLocked,
    planModules,
    allowedModules,
    companyAdmin: isCompanyAdmin(userInfo),
    isSuperAdmin: roles.some((role) => role?.name === SUPER_ADMIN),
    canShowUsers: canShowUserManagementNav(userInfo),
    canAccessPlanModule: canAccessPlanModule || (() => false),
    sidebarTokenAllowed: (token) => sidebarTokenAllowed(token, safeAccess),
    settingsSection: allowedModules.includes('Configuration') || (canAccessPlanModule ? canAccessPlanModule('settings') : false),
    allow: (permission) => !profileLocked || hasPermission(safeAccess, permission),
    hasPermission: (permission) => hasPermission(safeAccess, permission),
    routeAllowed: (path) => routeAllowed(path, safeAccess),
  }
}

export function partitionFeatures(ctx) {
  const available = []
  const unavailable = []
  FEATURES.forEach((feature) => {
    if (feature.visible(ctx)) available.push(feature)
    else unavailable.push(feature)
  })
  return { available, unavailable }
}
